import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Loader2, Menu } from 'lucide-react';
import { Sidebar, Logo } from '../components/admin/Sidebar';
import { DashboardTab, StatsTab } from '../components/admin/DashboardTabs';
import { MessagesTab, ChatsTab, FAQTab, CalendarTab, BookingsTab } from '../components/admin/ContentTabs';
import { GalleryTab, ReviewsTab, BlogTab } from '../components/admin/MediaTabs';
import { SEOTab, GEOTab, AISEOTab, AITab, SettingsTab } from '../components/admin/SettingsTabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    if (user && user.role === 'admin') {
      fetchStats();
    }
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardTab stats={stats} loading={statsLoading} />;
      case 'calendar': return <CalendarTab />;
      case 'bookings': return <BookingsTab />;
      case 'messages': return <MessagesTab />;
      case 'blog': return <BlogTab />;
      case 'gallery': return <GalleryTab />;
      case 'reviews': return <ReviewsTab />;
      case 'chats': return <ChatsTab />;
      case 'faq': return <FAQTab />;
      case 'stats': return <StatsTab stats={stats} loading={statsLoading} />;
      case 'aiseo': return <AISEOTab />;
      case 'seo': return <SEOTab />;
      case 'geo': return <GEOTab />;
      case 'ai': return <AITab />;
      case 'settings': return <SettingsTab />;
      default: return <DashboardTab stats={stats} loading={statsLoading} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A]">
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        isMobileOpen={mobileMenuOpen}
        setMobileOpen={setMobileMenuOpen}
      />

      <header className="md:hidden sticky top-0 z-40 bg-[#05050A]/95 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center justify-between px-4 h-16">
          <Logo />
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      <main className="md:ml-64 p-6 md:p-8" data-testid="admin-content">
        {renderContent()}
      </main>
    </div>
  );
};

export default AdminPage;
